import { Injectable } from '@angular/core';
import {
  HttpContextToken, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse
} from '@angular/common/http';
import { defer, finalize, map, Observable, of, shareReplay, tap } from 'rxjs';
import { jwtDecode } from 'jwt-decode';

export const SKIP_ACTIVE_LOOKUP_CACHE = new HttpContextToken<boolean>(() => false);

@Injectable()
export class ActiveLookupCacheInterceptor implements HttpInterceptor {
  private readonly ttlMs = 30_000;
  private readonly maxEntries = 100;
  private readonly responses = new Map<string, { response: HttpResponse<any>; expires: number }>();
  private readonly pending = new Map<string, Observable<HttpEvent<any>>>();
  private authorization: string | null = null;
  private generation = 0;
  private activeWrites = 0;

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return defer(() => this.handle(req, next));
  }

  private handle(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const authorization = req.headers.get('Authorization');
    if (authorization !== this.authorization) {
      this.clear();
      this.authorization = authorization;
    }

    if (!['GET', 'HEAD', 'OPTIONS'].includes(req.method)) {
      // Invalidate at both ends: reads during a write must not repopulate old data.
      this.clear();
      this.activeWrites++;
      return defer(() => next.handle(req)).pipe(finalize(() => {
        this.activeWrites--;
        this.clear();
      }));
    }

    const tokenExpires = this.tokenExpires(authorization);
    if (req.method !== 'GET' || !/\/getactive\/?$/i.test(req.url.split('?')[0]) ||
      req.responseType !== 'json' || req.reportProgress || this.activeWrites > 0 ||
      req.context.get(SKIP_ACTIVE_LOOKUP_CACHE) ||
      /no-cache|no-store|max-age=0/i.test(req.headers.get('Cache-Control') ?? '') ||
      tokenExpires <= Date.now()) {
      return next.handle(req);
    }

    // Include query parameters and headers (authorization, tenant, language, etc.).
    const key = JSON.stringify([req.urlWithParams, req.withCredentials,
    req.headers.keys().sort().map(name => [name.toLowerCase(), req.headers.getAll(name)])]);
    for (const [entryKey, entry] of this.responses) {
      if (entry.expires <= Date.now()) this.responses.delete(entryKey);
    }
    const cached = this.responses.get(key);
    if (cached) return of(this.copy(cached.response));

    let request = this.pending.get(key);
    if (!request) {
      const generation = this.generation;
      request = defer(() => next.handle(req)).pipe(
        tap(event => {
          if (event instanceof HttpResponse && event.status === 200 &&
            generation === this.generation && this.activeWrites === 0 &&
            this.isSuccess(event.body) &&
            !/no-store|no-cache/i.test(event.headers.get('Cache-Control') ?? '')) {
            if (this.responses.size >= this.maxEntries) {
              this.responses.delete(this.responses.keys().next().value!);
            }
            this.responses.set(key, {
              response: this.copy(event),
              expires: Math.min(Date.now() + this.ttlMs, tokenExpires)
            });
          }
        }),
        finalize(() => {
          // A pre-write request must not remove a newer request with the same key.
          if (this.pending.get(key) === request) this.pending.delete(key);
        }),
        shareReplay({ bufferSize: 1, refCount: true })
      );
      this.pending.set(key, request);
    }
    // Screens sometimes edit lookup arrays; each subscriber gets its own copy.
    return request.pipe(map(event => event instanceof HttpResponse ? this.copy(event) : event));
  }

  private clear(): void {
    this.generation++;
    this.responses.clear();
    this.pending.clear();
  }

  private tokenExpires(authorization: string | null): number {
    if (!authorization?.startsWith('Bearer ')) return 0;
    try {
      return (jwtDecode(authorization.substring(7)).exp ?? 0) * 1000;
    } catch {
      return 0;
    }
  }

  private isSuccess(body: any): boolean {
    // Some APIs return an error envelope with HTTP 200.
    const status = body?.statusCode ?? body?.StatusCode;
    return status == null || (Number(status) >= 200 && Number(status) < 300);
  }

  private copy(response: HttpResponse<any>): HttpResponse<any> {
    return response.clone({ body: structuredClone(response.body) });
  }
}
